<template>
  <Teleport to="body">
    <div
      v-if="builder.ativo.value"
      data-tour-builder-badge
      class="builder-badge"
      role="status"
      aria-live="polite"
    >
      Tour Builder ativo (Alt+Shift+T)
    </div>

    <div
      v-if="builder.ativo.value"
      ref="painelRef"
      data-tour-builder-panel
      class="builder-panel"
      :class="{ 'builder-panel--arrastando': arrastando }"
      :style="estiloPainel"
    >
      <div class="builder-header" @pointerdown="iniciarArraste">
        <span>{{ builder.tourExistenteId.value ? 'Editar Tour' : 'Tour Builder' }}</span>
        <button type="button" class="builder-close" @click="cancelarEdicao">✕</button>
      </div>

      <div class="builder-body">
        <p v-if="builder.tourExistenteId.value" class="builder-edit-hint">
          Você está editando um tour já publicado. Ao publicar, o tour anterior será substituído.
        </p>
        <p v-if="builder.passos.value.length === 0" class="builder-hint">
          <strong>Ctrl+clique</strong> (ou <strong>Cmd+clique</strong>) em qualquer elemento da página para adicionar um passo.
          <br /><br />
          Navegue para outras telas — o Builder permanece ativo. Clique nos elementos de cada tela para adicionar passos.
          <br /><br />
          Dica: prefira elementos com <code>data-tour-id</code> para seletor estável.
        </p>

        <TourBuilderStep
          v-for="(passo, idx) in builder.passos.value"
          :key="passo.id"
          :passo="passo"
          :index="idx"
          :total="builder.passos.value.length"
          :editando="builder.editando.value === passo.id"
          @editar="builder.editando.value = passo.id"
          @atualizar="(campo, valor) => Object.assign(passo, { [campo]: valor })"
          @remover="builder.removerPasso(passo.id)"
          @mover="(direcao) => builder.moverPasso(idx, direcao)"
        />
      </div>

      <div v-if="builder.passos.value.length > 0" class="builder-footer">
        <input
          v-model="builder.nomeTour.value"
          placeholder="Nome do tour"
          class="campo-texto"
        />
        <label class="campo-rotulo" for="builder-rota-entrada">
          Tela de entrada (onde o tour começa a aparecer)
        </label>
        <input
          id="builder-rota-entrada"
          v-model="builder.rotaPattern.value"
          placeholder="Rota (ex: /dashboard)"
          class="campo-texto"
          @input="builder.rotaPatternTravada.value = true"
        />
        <div v-if="erro" class="campo-erro">{{ erro }}</div>
        <button
          type="button"
          class="btn-publicar"
          :disabled="publicando"
          @click="publicar"
        >
          {{ rotuloBotaoPublicar }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { sanitizeHtml } from '~/src/helpers/sanitizeHtml'
import { useTourBuilder } from '../composables/useTourBuilder.js'
import TourBuilderStep from './TourBuilderStep.vue'

const builder = useTourBuilder()
const publicando = ref(false)
const erro = ref(null)

const painelRef = ref(null)
const arrastando = ref(false)
const posicaoPersonalizada = ref(null)
let offsetArraste = { x: 0, y: 0 }

const estiloPainel = computed(() => {
  if (!posicaoPersonalizada.value) return {}
  return {
    top: `${posicaoPersonalizada.value.top}px`,
    left: `${posicaoPersonalizada.value.left}px`,
    right: 'auto',
    bottom: 'auto'
  }
})

function iniciarArraste(e) {
  if (e.button !== 0) return
  if (e.target.closest('.builder-close')) return
  const painel = painelRef.value
  if (!painel) return

  const rect = painel.getBoundingClientRect()
  offsetArraste = { x: e.clientX - rect.left, y: e.clientY - rect.top }
  arrastando.value = true
  window.addEventListener('pointermove', moverArraste)
  window.addEventListener('pointerup', finalizarArraste)
}

function moverArraste(e) {
  if (!arrastando.value) return
  const painel = painelRef.value
  if (!painel) return

  const margem = 8
  const largura = painel.offsetWidth
  const altura = painel.offsetHeight
  const limiteX = Math.max(margem, window.innerWidth - largura - margem)
  const limiteY = Math.max(margem, window.innerHeight - altura - margem)

  posicaoPersonalizada.value = {
    left: Math.min(Math.max(e.clientX - offsetArraste.x, margem), limiteX),
    top: Math.min(Math.max(e.clientY - offsetArraste.y, margem), limiteY)
  }
}

function finalizarArraste() {
  arrastando.value = false
  window.removeEventListener('pointermove', moverArraste)
  window.removeEventListener('pointerup', finalizarArraste)
}

const rotuloBotaoPublicar = computed(() => {
  if (publicando.value) return 'Salvando...'
  return builder.tourExistenteId.value ? 'Atualizar Tour' : 'Publicar Tour'
})

async function ativarComEdicao() {
  builder.ativar()
  if (builder.passos.value.length > 0) return

  await builder.carregarTourEditavel(window.location.pathname)
}

function cancelarEdicao() {
  builder.desativar()
  builder.limpar()
}

function onKeydown(e) {
  if (e.altKey && e.shiftKey && e.code === 'KeyT') {
    e.preventDefault()
    if (builder.ativo.value) {
      cancelarEdicao()
    } else {
      ativarComEdicao()
    }
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('pointermove', moverArraste)
  window.removeEventListener('pointerup', finalizarArraste)
})

async function publicar() {
  erro.value = null
  if (!builder.nomeTour.value || !builder.rotaPattern.value) {
    erro.value = 'Preencha o nome e a rota do tour.'
    return
  }

  const passosSemTexto = builder.passos.value.filter(
    (p) => !p.titulo || !p.descricao
  )
  if (passosSemTexto.length > 0) {
    erro.value = `Preencha título e descrição de todos os passos (faltando: ${passosSemTexto.length}).`
    return
  }

  try {
    publicando.value = true
    if (builder.tourExistenteId.value) {
      await builder.atualizar(builder.tourExistenteId.value, builder.nomeTour.value, builder.rotaPattern.value)
    } else {
      await builder.publicar(builder.nomeTour.value, builder.rotaPattern.value)
    }
    const action = builder.tourExistenteId.value ? 'atualizado' : 'publicado'
    mainoAlert({
      title:  `Tour ${action}!`,
      buttonClose: true,
      type:'alert',
      confirmText: 'Ok',
      textConfirm: `Tour ${sanitizeHtml(builder.nomeTour.value)} ${action} com sucesso!`
    })
    builder.limpar()
    builder.desativar()
  } catch (e) {
    erro.value = e.message || 'Erro ao publicar.'
  } finally {
    publicando.value = false
  }
}
</script>

<style scoped>
.builder-badge {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 16px;
  background: #5340b7;
  color: #fff;
  border-radius: 999px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  font-size: 13px;
  font-weight: 500;
  z-index: 99997;
  pointer-events: none;
}
.builder-panel {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 320px;
  max-height: 80vh;
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 99999;
  font-size: 13px;
}
.builder-panel--arrastando {
  user-select: none;
}
.builder-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  font-weight: 600;
  font-size: 14px;
  cursor: move;
  touch-action: none;
}
.builder-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
}
.builder-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}
.builder-footer {
  padding: 12px;
  border-top: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.builder-hint {
  color: #888;
  font-size: 12px;
  text-align: center;
  padding: 16px 0;
  line-height: 1.5;
}
.builder-edit-hint {
  background: var(--alert-10);
  border: 1px solid var(--alert-30);
  color: var(--alert-50);
  padding: 8px 10px;
  border-radius: var(--border-radius-8);
  font-size: 12px;
  margin-bottom: 8px;
}
.builder-hint code {
  background: #f5f5f5;
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 11px;
}
.campo-texto {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  box-sizing: border-box;
}
.campo-rotulo {
  font-size: 11px;
  color: var(--neutral-gray-50);
  margin-bottom: -4px;
}
.campo-erro {
  color: #b00020;
  font-size: 12px;
}
.btn-publicar {
  width: 100%;
  padding: 8px;
  background: #5340b7;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
}
.btn-publicar:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
